import React from 'react';
import { get, getTranslation, isDefined, LANGUAGE, type TFnResources } from '@nfctron/nexus-core';
import { Toaster as Sonner, toast as sonnerToast, type ExternalToast } from 'sonner';

import { useIntl } from '../i18n';
import { cn } from '../utils';

/* -------------------------------------------------------------------------------------------------
 * Toaster
 * -----------------------------------------------------------------------------------------------*/

type ToasterProps = React.ComponentProps<typeof Sonner>;

const UiToaster = ({ ...props }: ToasterProps) => (
	<Sonner
		theme="light"
		className="toaster group"
		toastOptions={{
			classNames: {
				toast: cn(
					'group toast group-[.toaster]:bg-white group-[.toaster]:text-zinc-950 group-[.toaster]:border-zinc-200 group-[.toaster]:shadow-lg',
					'dark:group-[.toaster]:bg-zinc-950 dark:group-[.toaster]:text-zinc-50 dark:group-[.toaster]:border-zinc-800',
				),
				description: cn('group-[.toast]:text-zinc-500', 'dark:group-[.toast]:text-zinc-400'),
				actionButton: cn(
					'group-[.toast]:bg-zinc-900 group-[.toast]:text-zinc-50',
					'dark:group-[.toast]:bg-zinc-50 dark:group-[.toast]:text-zinc-900',
				),
				cancelButton: cn(
					'group-[.toast]:bg-zinc-100 group-[.toast]:text-zinc-500',
					'dark:group-[.toast]:bg-zinc-800 dark:group-[.toast]:text-zinc-400',
				),
			},
		}}
		{...props}
	/>
);

const resources = {
	[LANGUAGE.CZECH]: {
		'toastError.copyText': 'Data se nepodařilo zkopírovat. Zkuste to prosím znovu.',
		'toastError.createText': 'Vytvoření se nezdařilo. Zkuste to prosím znovu.',
		'toastError.deleteText': 'Odstranění se nezdařilo. Zkuste to prosím znovu.',
		'toastError.downloadText': 'Data se nepodařilo stáhnout. Zkuste to prosím znovu.',
		'toastError.saveText': 'Data se nepodařilo uložit. Zkuste to prosím znovu.',
		'toastError.text': 'Operace selhala. Zkuste to prosím znovu později.',
		'toastError.updateText': 'Úprava se nezdařila. Zkuste to prosím znovu.',
		'toastError.loadText': 'Načítání dat selhalo. Zkuste to prosím znovu.',
		'toastLoading.copyText': 'Kopírování probíhá, prosím čekejte...',
		'toastLoading.createText': 'Vytváření probíhá, prosím čekejte...',
		'toastLoading.deleteText': 'Odstraňování probíhá, prosím čekejte...',
		'toastLoading.downloadText': 'Stahování probíhá, prosím čekejte...',
		'toastLoading.saveText': 'Ukládání probíhá, prosím čekejte...',
		'toastLoading.text': 'Operace probíhá, prosím čekejte...',
		'toastLoading.updateText': 'Úprava probíhá, prosím čekejte...',
		'toastLoading.loadText': 'Data se načítají, prosím čekejte...',
		'toastSuccess.copyText': 'Text byl úspěšně zkopírován',
		'toastSuccess.createText': 'Vytvoření proběhlo úspěšně.',
		'toastSuccess.deleteText': 'Odstranění proběhlo úspěšně.',
		'toastSuccess.downloadText': 'Data byla úspěšně stažena',
		'toastSuccess.saveText': 'Data byla úspěšně uložena.',
		'toastSuccess.text': 'Operace proběhla úspěšně.',
		'toastSuccess.updateText': 'Úprava proběhla úspěšně.',
		'toastSuccess.loadText': 'Data byla úspěšně načtena.',
	},
	[LANGUAGE.SLOVAK]: {
		'toastError.copyText': 'Dáta sa nepodarilo skopírovať. Skúste to prosím znovu.',
		'toastError.createText': 'Vytvorenie sa nepodarilo. Skúste to prosím znovu.',
		'toastError.deleteText': 'Odstránenie sa nepodarilo. Skúste to prosím znovu.',
		'toastError.downloadText': 'Dáta sa nepodarilo stiahnuť. Skúste to prosím znovu.',
		'toastError.saveText': 'Dáta sa nepodarilo uložiť. Skúste to prosím znovu.',
		'toastError.text': 'Operácia zlyhala. Skúste to prosím znovu neskôr.',
		'toastError.updateText': 'Úprava sa nepodarila. Skúste to prosím znovu.',
		'toastError.loadText': 'Načítanie dát zlyhalo. Skúste to prosím znovu.',
		'toastLoading.copyText': 'Kopírovanie prebieha, prosím čakajte...',
		'toastLoading.createText': 'Vytváranie prebieha, prosím čakajte...',
		'toastLoading.deleteText': 'Odstraňovanie prebieha, prosím čakajte...',
		'toastLoading.downloadText': 'Sťahovanie prebieha, prosím čakajte...',
		'toastLoading.saveText': 'Ukladanie prebieha, prosím čakajte...',
		'toastLoading.text': 'Operácia prebieha, prosím čakajte...',
		'toastLoading.updateText': 'Úprava prebieha, prosím čakajte...',
		'toastLoading.loadText': 'Načítanie dát prebieha, prosím čakajte...',
		'toastSuccess.copyText': 'Text bol úspešne skopírovaný',
		'toastSuccess.createText': 'Vytvorenie prebehlo úspešne.',
		'toastSuccess.deleteText': 'Odstránenie prebehlo úspešne.',
		'toastSuccess.downloadText': 'Dáta boli úspešne stiahnuté',
		'toastSuccess.saveText': 'Dáta boli úspešne uložené.',
		'toastSuccess.text': 'Operácia prebehla úspešne.',
		'toastSuccess.updateText': 'Úprava prebehla úspešne.',
		'toastSuccess.loadText': 'Dáta boli úspešne načítané.',
	},
	[LANGUAGE.ENGLISH]: {
		'toastError.copyText': 'The data could not be copied. Please try again.',
		'toastError.createText': 'The creation failed. Please try again.',
		'toastError.deleteText': 'The removal failed. Please try again.',
		'toastError.downloadText': 'The data could not be downloaded. Please try again.',
		'toastError.saveText': 'The data could not be saved. Please try again.',
		'toastError.text': 'The operation failed. Please try again later.',
		'toastError.updateText': 'The modification failed. Please try again.',
		'toastError.loadText': 'Data loading failed. Please try again.',
		'toastLoading.copyText': 'Copying in progress, please stand by...',
		'toastLoading.createText': 'Creation in progress, please stand by...',
		'toastLoading.deleteText': 'Removal in progress, please stand by...',
		'toastLoading.downloadText': 'Download is in progress, please stand by...',
		'toastLoading.saveText': 'Storage is in progress, please stand by...',
		'toastLoading.text': 'Surgery in progress, please stand by...',
		'toastLoading.updateText': 'Editing in progress, please stand by...',
		'toastLoading.loadText': 'Data loading in progress, please stand by...',
		'toastSuccess.copyText': 'Text was successfully copied to your clipboard',
		'toastSuccess.createText': 'The creation was successful.',
		'toastSuccess.deleteText': 'The removal was successful.',
		'toastSuccess.downloadText': 'The data has been successfully downloaded',
		'toastSuccess.saveText': 'The data has been successfully saved.',
		'toastSuccess.text': 'The operation was successful.',
		'toastSuccess.updateText': 'The modification was successful.',
		'toastSuccess.loadText': 'Data has been successfully loaded.',
	},
	[LANGUAGE.GERMAN]: {
		'toastError.copyText': 'Die Daten konnten nicht kopiert werden. Bitte versuchen Sie es erneut.',
		'toastError.createText': 'Die Erstellung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
		'toastError.deleteText': 'Die Entfernung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
		'toastError.downloadText': 'Die Daten konnten nicht heruntergeladen werden. Bitte versuchen Sie es erneut.',
		'toastError.saveText': 'Die Daten konnten nicht gespeichert werden. Bitte versuchen Sie es erneut.',
		'toastError.text': 'Der Vorgang ist fehlgeschlagen. Bitte versuchen Sie es später noch einmal.',
		'toastError.updateText': 'Die Änderung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
		'toastError.loadText': 'Das Laden der Daten ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
		'toastLoading.copyText': 'Kopiervorgang läuft, bitte bereithalten...',
		'toastLoading.createText': 'Erstellung in Arbeit, bitte bereithalten...',
		'toastLoading.deleteText': 'Umzug im Gange, bitte bereithalten...',
		'toastLoading.downloadText': 'Der Download ist in Arbeit, bitte warten Sie...',
		'toastLoading.saveText': 'Die Lagerung ist im Gange, bitte bleiben Sie dran...',
		'toastLoading.text': 'Operation im Gange, bitte bereithalten...',
		'toastLoading.updateText': 'Bearbeitung im Gange, bitte bereithalten...',
		'toastLoading.loadText': 'Daten werden geladen, bitte warten...',
		'toastSuccess.copyText': 'Text wurde erfolgreich in die Zwischenablage kopiert',
		'toastSuccess.createText': 'Die Gründung war erfolgreich.',
		'toastSuccess.deleteText': 'Der Umzug war erfolgreich.',
		'toastSuccess.downloadText': 'Die Daten wurden erfolgreich heruntergeladen',
		'toastSuccess.saveText': 'Die Daten wurden erfolgreich gespeichert.',
		'toastSuccess.text': 'Die Operation war erfolgreich.',
		'toastSuccess.updateText': 'Die Änderung war erfolgreich.',
		'toastSuccess.loadText': 'Daten wurden erfolgreich geladen.',
	},
	[LANGUAGE.FRENCH]: {} as any,
	[LANGUAGE.SPANISH]: {} as any,
	[LANGUAGE.RUSSIAN]: {} as any,
	[LANGUAGE.NORWEGIAN]: {} as any,
	[LANGUAGE.SWEDISH]: {} as any,
} as const satisfies TFnResources<any>;
const useTranslationsScoped = () => {
	const language = useIntl();
	return getTranslation(language.languageId, resources);
};

const useToast = () => {
	const { t } = useTranslationsScoped();

	/** toast presets */
	const toastPresets = {
		delete: {
			success: t('toastSuccess.deleteText', null),
			error: t('toastError.deleteText', null),
			loading: t('toastLoading.deleteText', null),
		},
		update: {
			success: t('toastSuccess.updateText', null),
			error: t('toastError.updateText', null),
			loading: t('toastLoading.updateText', null),
		},
		create: {
			success: t('toastSuccess.createText', null),
			error: t('toastError.createText', null),
			loading: t('toastLoading.createText', null),
		},
		save: {
			success: t('toastSuccess.saveText', null),
			error: t('toastError.saveText', null),
			loading: t('toastLoading.saveText', null),
		},
		download: {
			success: t('toastSuccess.downloadText', null),
			error: t('toastError.downloadText', null),
			loading: t('toastLoading.downloadText', null),
		},
		copy: {
			success: t('toastSuccess.copyText', null),
			error: t('toastError.copyText', null),
			loading: t('toastLoading.copyText', null),
		},
		load: {
			success: t('toastSuccess.loadText', null),
			error: t('toastError.loadText', null),
			loading: t('toastLoading.loadText', null),
		},
	} as const;
	type ToastPreset = keyof typeof toastPresets;
	const isToastPreset = (message: any): message is ToastPreset => isDefined(message) && isDefined(get(toastPresets, message));

	/** info toast */
	const toastInfo = (message?: ToastPreset | React.ReactNode, data?: ExternalToast) => {
		const msgText = isToastPreset(message) ? toastPresets[message].success : message ?? t('toastSuccess.text', null);
		return sonnerToast.info(msgText, data);
	};

	/** success toast */
	const toastSuccess = (message?: ToastPreset | React.ReactNode, data?: ExternalToast) => {
		const msgText = isToastPreset(message) ? toastPresets[message].success : message ?? t('toastSuccess.text', null);
		return sonnerToast.success(msgText, { ...data });
	};

	/** error toast */
	const toastError = (message?: ToastPreset | React.ReactNode, data?: ExternalToast) => {
		const msgText = isToastPreset(message) ? toastPresets[message].error : message ?? t('toastError.text', null);
		return sonnerToast.error(msgText, { ...data });
	};

	/** warning toast */
	const toastWarning = (message?: ToastPreset | React.ReactNode, data?: ExternalToast) => {
		const msgText = isToastPreset(message) ? toastPresets[message].error : message ?? t('toastError.text', null);
		return sonnerToast.warning(msgText, { ...data });
	};

	/** loading toast */
	const toastLoading = (message: React.ReactNode, data?: ExternalToast) => {
		const msgText = message ?? t('toastLoading.text', null);
		return sonnerToast.loading(msgText, data);
	};

	/** promise toast */
	const toastPromise = <ToastData extends any>(promise: PromiseT<ToastData>, data?: ToastPreset | PromiseData<ToastData>) => {
		const promiseData = isToastPreset(data) ? toastPresets[data] : data;
		return sonnerToast.promise(promise, {
			loading: t('toastLoading.text', null),
			success: t('toastSuccess.text', null),
			error: t('toastError.text', null),
			...promiseData,
		});
	};

	return {
		info: toastInfo,
		success: toastSuccess,
		error: toastError,
		warning: toastWarning,
		loading: toastLoading,
		promise: toastPromise,
		custom: sonnerToast.custom,
		message: sonnerToast.message,
		dismiss: sonnerToast.dismiss,
	};
};

type PromiseT<Data = any> = Promise<Data> | (() => Promise<Data>);
type PromiseData<ToastData = any> = ExternalToast & {
	loading?: string | React.ReactNode;
	success?: string | React.ReactNode | ((data: ToastData) => React.ReactNode | string);
	error?: string | React.ReactNode | ((error: any) => React.ReactNode | string);
	finally?: () => void | Promise<void>;
};

/* -----------------------------------------------------------------------------------------------*/

export { UiToaster, sonnerToast, useToast };
